import moment from 'moment';
import { useOrderStore } from 'store/orderStore';
import _ from 'lodash';
import {
  AdType,
  CreativeDeliverType,
  DeliverType,
  RtbCampaignPlanType,
  RTBCAMPAIGN_DEFAULT_AGE_MAX,
  RTBCAMPAIGN_DEFAULT_AGE_MIN,
} from 'core/rtbCampaign/RtbCampaign';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import {
  BudgetType,
  RunningPattern,
  SelectPattern,
} from 'core/rtbCampaign/singlePage/common';
import { BidMode, RoasStrategy } from 'core/rtbCampaign/singlePage/keyword';
import { Goal } from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useOutboundKeywordForm';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function useCreateOutboundKeyword() {
  const order = useOrderStore().order;

  const useCampaignStartDay = () => {
    const now = moment().startOf('hour').add(1, 'hour');
    const orderStartDate = moment(order.startDate);
    return now.isBefore(orderStartDate) ? orderStartDate : now;
  };

  const useCampaignEndDay = () => {
    return moment.min(
      moment(order.endDate).add(1, 'days').subtract(1, 'seconds'),
      moment(useCampaignStartDay())
        .add(13, 'days')
        .endOf('day')
        .subtract(1, 'seconds'),
    );
  };

  const getDefaultCampaign = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ) => {
    const ageLimitationPresets = [
      {
        type: 'age_min',
        value: RTBCAMPAIGN_DEFAULT_AGE_MIN,
      },
      {
        type: 'age_max',
        value: RTBCAMPAIGN_DEFAULT_AGE_MAX,
      },
    ];

    const limitationPreSet = {
      include: [...ageLimitationPresets],
    };

    const defaultCampaign = {
      basic: {
        advertiserId: order.advertiserId,
        orderId: order.id,
        adType: AdType.OUTBOUND_KEYWORD,
        name: order.projectName,
        budget: order.budgetBalance,
        dailyTargetBudget: order.budgetBalance,
        spents: 0,
        expectedSpent: 0,
        bidPrice: undefined,
        startDate: startDate.format(DATE_FORMAT),
        endDate: endDate.format(DATE_FORMAT),
        checkpoints: [],
        creativeDeliverType: CreativeDeliverType.OPTIMIZE,
        priceModel: RtbCampaignPlanType.RS_CPC,
        optimize: L2ObjectOptimizationGoal.CLICKS,
        deliverType: DeliverType.ACCELERATED,
        bidStrategy: BidStrategy.LOWEST_COST_WITH_BID_CAP,
        bidMode: BidMode.AUTO,
        budgetType: BudgetType.TOTAL,
        webBrandNo: null,
        brandName: null,
        selectPattern: SelectPattern.AUTO,
        runningPattern: RunningPattern.LIMITED,
        outboundInfo: {
          goal: Goal.LINK_CLICKS,
        },
        roasOption: {
          roasStrategy: RoasStrategy.HIGH_ROAS,
          roas: null,
        },
      },
      limitations: _.cloneDeep(limitationPreSet),
      products: [],
      autoProducts: [],
      keywords: [],
    };
    return defaultCampaign;
  };

  const startDate = useCampaignStartDay();
  const endDate = useCampaignEndDay();

  return { getDefaultCampaign: () => getDefaultCampaign(startDate, endDate) };
}

export default useCreateOutboundKeyword;
